import { animateValue, extractCurrencyAndValue } from '../lib/moneyTicker'

/**
 * SmProgressBar is a custom HTMLElement that provides a progress bar functionality
 * along with value animation. It also listens for 'cart:quantity:update' events to
 * adjust its progress and value based on the cart updates.
 */
class SmProgressBar extends window.HTMLElement {
  staticId = null
  initialized = false
  unlocked = false

  constructor() {
    super()
    this.staticId = this.dataset.static
    this.unlocked = this.dataset.unlocked === 'true'
    // Bind the onCartUpdate method to the current instance
    this.boundOnCartUpdate = this.onCartUpdate.bind(this)
  }

  /**
   * Event handler for 'cart:quantity:update' events.
   * It updates the progress and value of the progress bar based on cart updates.
   * @param {Event} event - The event object with cart update details.
   */
  onCartUpdate(event) {
    const children = event.detail?.newChildren
    if (children) {
      children.forEach((child) => {
        if (this.staticId === child.dataset?.static) {
          const newProgress = child.dataset?.progress
          if (newProgress) {
            this.style.setProperty('--progress', newProgress)

            const currentData = extractCurrencyAndValue(
              this.querySelector('.progress-bar__label').textContent
            )
            const newData = extractCurrencyAndValue(child.dataset?.away)

            animateValue(
              this.querySelector('.progress-bar__label'),
              currentData.symbol,
              currentData.value,
              newData.symbol,
              newData.value,
              300
            )
          }

          // Update unlocked state based on the data-unlocked attribute from the cart
          if (child.dataset?.unlocked === 'true') {
            this.classList.add('unlocked')
            this.switchMessage(false)
          } else {
            this.classList.remove('unlocked')
            this.switchMessage(true)
          }
        }
      })
    }
  }

  switchMessage(locked) {
    if (locked) {
      this.querySelector('[data-unlocked-title]').textContent =
        this.querySelector('[data-unlocked-title]').dataset.originalTitle
    } else {
      if (
        this.querySelector('[data-unlocked-title]').dataset.unlockedTitle !== ''
      ) {
        this.querySelector('[data-unlocked-title]').textContent =
          this.querySelector('[data-unlocked-title]').dataset.unlockedTitle
      }
    }
  }

  /**
   * Initializes the progress bar by setting its progress and value.
   * If the bar is unlocked, it adds an 'unlocked' class.
   */
  initializeProgressBar() {
    const newProgress = this.dataset?.progress
    if (newProgress && !this.initialized) {
      this.style.setProperty('--progress', newProgress)

      const currentData = extractCurrencyAndValue(
        this.querySelector('.progress-bar__label').textContent
      )
      const newData = extractCurrencyAndValue(this.dataset?.away)

      animateValue(
        this.querySelector('.progress-bar__label'),
        currentData.symbol,
        currentData.value,
        newData.symbol,
        newData.value,
        300
      )
      this.initialized = true
    }

    // Set initial unlocked state
    if (this.unlocked) {
      this.classList.add('unlocked')
      this.switchMessage(false)
    } else {
      this.switchMessage(true)
    }
  }

  connectedCallback() {
    this.initializeProgressBar()
    // Attach the event listener for 'cart:quantity:update' events
    document.addEventListener('cart:quantity:update', this.boundOnCartUpdate)
  }

  disconnectedCallback() {
    // Detach the event listener when the element is removed from the DOM
    document.removeEventListener('cart:quantity:update', this.boundOnCartUpdate)
  }
}

// Register the custom element 'sm-progress-bar'
window.customElements.define('sm-progress-bar', SmProgressBar)
